import { zodResolver } from "@hookform/resolvers/zod"
import * as React from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { api, ApiError } from "@/api/client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

const schema = z.object({
  full_name: z.string().min(2, "Ad-soyad gerekli."),
  email: z.string().email("Geçerli bir e-posta girin."),
  phone: z.string().optional(),
  linkedin_url: z.string().url("Geçerli bir LinkedIn URL gir.").optional().or(z.literal("")),
  cover_letter: z.string().max(20000).optional().or(z.literal("")),
})

type FormValues = z.infer<typeof schema>

type SignResp = { upload_path: string; upload_url: string; method: "PUT" }

export function ApplyForm({ jobId }: { jobId: string }) {
  const fileRef = React.useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = React.useState<string | null>(null)
  const [submitted, setSubmitted] = React.useState(false)
  const form = useForm<FormValues>({ resolver: zodResolver(schema) })

  async function onSubmit(values: FormValues) {
    const file = fileRef.current?.files?.[0]
    if (!file) {
      toast.error("Lütfen CV dosyanı yükle (PDF).")
      return
    }
    if (file.type !== "application/pdf") {
      toast.error("Sadece PDF kabul ediliyor.")
      return
    }
    try {
      const signed = await api<SignResp>(`/public/apply/${jobId}/sign`, {
        method: "POST",
        body: { file_name: file.name, mime_type: file.type, size: file.size },
      })
      const up = await fetch(signed.upload_url, {
        method: signed.method,
        body: file,
        headers: { "Content-Type": file.type },
      })
      if (!up.ok) throw new Error("Dosya yüklenemedi.")
      await api(`/public/apply/${jobId}`, {
        method: "POST",
        body: {
          ...values,
          upload_path: signed.upload_path,
          file_name: file.name,
          mime_type: file.type,
          size: file.size,
        },
      })
      setSubmitted(true)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Başvuru gönderilemedi.")
    }
  }

  if (submitted) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        Başvurun alındı. Geri dönüş için e-posta adresini takip et.
      </div>
    )
  }

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
      <Field label="Ad Soyad" error={form.formState.errors.full_name?.message}>
        <Input {...form.register("full_name")} />
      </Field>
      <Field label="E-posta" error={form.formState.errors.email?.message}>
        <Input type="email" {...form.register("email")} />
      </Field>
      <Field label="Telefon (opsiyonel)">
        <Input {...form.register("phone")} />
      </Field>
      <Field label="LinkedIn (opsiyonel)" error={form.formState.errors.linkedin_url?.message}>
        <Input type="url" {...form.register("linkedin_url")} />
      </Field>
      <Field label="Ön yazı (opsiyonel)">
        <Textarea rows={5} {...form.register("cover_letter")} />
      </Field>
      <div className="space-y-1.5">
        <Label>CV (PDF, en fazla 20 MB)</Label>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          className="sr-only"
          onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
        />
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start font-normal"
          onClick={() => fileRef.current?.click()}
        >
          <span className={cn("truncate", !fileName && "text-muted-foreground")}>
            {fileName ?? "Dosya seç (PDF)"}
          </span>
        </Button>
      </div>
      <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? "Gönderiliyor…" : "Başvuruyu gönder"}
      </Button>
    </form>
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}
